import type { AtelierActorHandle, AtelierActorRuntime, ScreenContext } from '../../shared';
import { h } from '../../ui';

/** Uses the real saved GLB recipe and the same equipped appearance as combat. */
export function createHeroStage(ctx: ScreenContext) {
  const root = h('div', { class: 'mc-build__stage', attrs: { 'aria-label': ctx.t('equipment.fallback') } });
  let runtime: AtelierActorRuntime | null = null, handle: AtelierActorHandle | null = null;
  let fallback: ReturnType<NonNullable<ScreenContext['services']['creatures']['portrait']>> | null = null;
  let disposed = false, loading = false, generation = 0, key = '', bounds = { minY: 0, maxY: 1, radius: .5 };
  let ready: Promise<void> = Promise.resolve();
  const release = () => { handle?.dispose(); runtime?.dispose(); fallback?.dispose(); handle = null; runtime = null; fallback = null; };
  const update = () => {
    const hero = ctx.services.creatures.active(); if (!hero) return;
    const base = ctx.services.creatures.appearance?.(hero.id);
    const next = base ? ctx.services.equipment?.appearance(base) ?? base : null;
    const nextKey = JSON.stringify(next ?? hero.dna);
    if (nextKey === key) return;
    key = nextKey; const token = ++generation; release(); loading = true;
    root.dataset.loading = 'true'; root.setAttribute('aria-busy', 'true'); root.classList.remove('is-live');
    root.replaceChildren(h('span', { class: 'mc-build__loading', attrs: { role: 'status' } }, ctx.t('equipment.loading')));
    ready = (async () => {
      let pending: AtelierActorRuntime | null = null;
      try {
        if (!ctx.renderer || !next) throw new Error('portrait fallback');
        pending = await ctx.services.creatures.createAtelierActor(next);
        if (disposed || token !== generation) return;
        const uploaded = await ctx.renderer.addAtelierActor(pending.asset);
        if (!uploaded) throw new Error('No free hero slot');
        if (disposed || token !== generation) { uploaded.dispose(); return; }
        runtime = pending; pending = null; handle = uploaded;
        bounds = runtime.sample(0, { action: 'idle', reducedMotion: true, yaw: .18 }).bounds;
        ctx.renderer.setScene({ kind: 'stage', backdrop: 'vault' });
        root.replaceChildren(); root.classList.add('is-live'); root.dataset.renderer = 'atelier';
      } catch {
        if (!disposed && token === generation) {
          fallback = ctx.services.creatures.portrait?.(hero.dna) ?? null;
          root.dataset.renderer = 'portrait'; root.replaceChildren(fallback?.canvas ?? h('span', { class: 'mc-life__initial' }, hero.dna.name.charAt(0)));
        }
      } finally {
        pending?.dispose();
        if (token === generation) { loading = false; delete root.dataset.loading; root.removeAttribute('aria-busy'); }
      }
    })();
  };
  return {
    root, update, get ready() { return ready; }, get loading() { return loading; },
    render(ms: number) {
      fallback?.render(ms);
      if (!runtime || !handle || !ctx.renderer || disposed) return;
      const rect = root.getBoundingClientRect(); if (!rect.width || !rect.height) return;
      const reduced = /^(min|reduced)$/.test(document.documentElement.dataset.motion ?? '');
      handle.setFrame(runtime.sample(reduced ? 0 : ms / 1000, { action: 'idle', reducedMotion: reduced, yaw: .18 }));
      const center = (bounds.minY + bounds.maxY) / 2;
      ctx.renderer.panel.set({ rect: { x: rect.x, y: rect.y, w: rect.width, h: rect.height }, frame: 'pointedArch', archK: .24 });
      ctx.renderer.camera.set({ eye: [0, center + .22, 6], target: [0, center, 0], fovYDeg: 25, dutchDeg: 0,
        orthoHalfH: Math.max((bounds.maxY - bounds.minY) / 2 + .06, bounds.radius * rect.height / rect.width + .08), near: .1, far: 30 });
      ctx.renderer.advanceTo({ presentMs: ms, worldMs: ms, vfxMs: ms });
    },
    debugState: () => ({ renderer: root.dataset.renderer, loading, appearance: runtime?.appearance ?? null }),
    dispose() { disposed = true; generation++; release(); ctx.renderer?.camera.set(null); },
  };
}
